import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Carregando } from '../../componentes/estados';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro } from '../../nucleo/api';
import type { PropsFormularioParceiro } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';
import type { ParceiroCrm } from './crm-comum';
import { FormContato, FormEmpresa } from './CrmFormularios';

/**
 * Ajustes do CRM (27/10, F1): o formulário de contato ou empresa do CRM publicado para outro módulo (`formularioParceiro`).
 * Editar lê o cadastro antes e exige `cadastros.editar`; criar exige `cadastros.criar`. Sem a permissão, não abre nada.
 */
export function FormularioParceiro(props: PropsFormularioParceiro) {
  const s = useSessao();
  if (!props.aberto || !s.pode(props.id ? 'cadastros.editar' : 'cadastros.criar')) return null;
  return <FormularioAberto {...props} />;
}

/**
 * rl0211-4 (SPEC-M1, ROB-M2): montado só com o formulário aberto e sem cache (`gcTime: 0`), cada abertura lê o cadastro de novo
 * e só então monta o formulário. Salvar com a leitura velha desfazia o que foi gravado desde então: o PATCH regrava os campos e
 * o `PUT /adicionais` troca as listas inteiras. Sem refazer a leitura com o formulário aberto, que guarda o valor da montagem.
 *
 * Rodada rfil-6 (ROB-M1): a garantia é a chave, única por abertura. O React Query só reaproveita dado ou leitura em voo pela chave, então nada de
 * uma abertura anterior chega aqui, venha do cache ou de uma resposta atrasada, e só esta função lê com ela. Fechar aborta a leitura (`signal`) só para poupar a rede.
 */
function FormularioAberto({ tipo, aberto, aoFechar, id, empresa, aoSalvar }: PropsFormularioParceiro) {
  const [abertura] = useState(() => crypto.randomUUID());
  const atual = useQuery({ queryKey: ['formulario-parceiro', abertura, id], queryFn: ({ signal }) => get<ParceiroCrm>(`/cadastros/parceiros/${id}`, undefined, { signal }), enabled: !!id, gcTime: 0, staleTime: 0, refetchOnWindowFocus: false, refetchOnReconnect: false });
  const { t } = useTranslation('cadastros');
  const toast = useToast();
  // rl0211-5 (ROB-B1): falhou a leitura, avisa e fecha, para o lápis voltar a funcionar. O erro vira texto uma vez só.
  // Rodada rfil-7 (ROB-B3): com o formulário já montado, releitura com erro não fecha a gaveta nem perde o digitado.
  const erro = atual.isError && !atual.data ? mensagemDeErro(atual.error) : null;
  useEffect(() => {
    if (!erro) return;
    toast.erro(erro);
    aoFechar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erro]);
  if (erro) return null;
  if (id && !atual.data)
    return (
      <Drawer aberto aoFechar={aoFechar} titulo={tipo === 'empresa' ? t('crm.empresas.editar') : t('crm.contatos.editar')}>
        <Carregando />
      </Drawer>
    );
  return tipo === 'empresa' ? (
    <FormEmpresa aberto={aberto} aoFechar={aoFechar} atual={atual.data ?? null} aoSalvar={aoSalvar} />
  ) : (
    <FormContato aberto={aberto} aoFechar={aoFechar} atual={atual.data ?? null} empresa={empresa ?? undefined} aoSalvar={aoSalvar} />
  );
}
